import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import SiteFooter from '../components/SiteFooter';
import { useLanguage } from '../context/LanguageContext';
import './About.css';

// Shfaq elementet me fade-in kur hyjnë në pamje.
const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const items = root.querySelectorAll('.reveal');
    if (!('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return ref;
};

const TIMELINE = [
  { key: 'tl.2021', year: '2021' },
  { key: 'tl.dev', year: '2021 – 2024' },
  { key: 'tl.2025', year: '2025' },
  { key: 'tl.today', year: '✦' },
];

const VALUES = ['val.quality', 'val.detail', 'val.satisfaction'];

const About = () => {
  const { t } = useLanguage();
  const pageRef = useReveal();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="about-page" ref={pageRef}>
      <Header />

      <section className="about-hero">
        <div className="about-hero-glow" aria-hidden="true" />
        <div className="about-container about-hero-inner">
          <span className="about-eyebrow reveal">{t('header.about')}</span>
          <h1 className="about-hero-title reveal">{t('about.hero.title')}</h1>
          <p className="about-hero-lead reveal">{t('about.hero.lead')}</p>
          <div className="about-scroll-hint reveal" aria-hidden="true" />
        </div>
      </section>

      <section className="about-section about-story">
        <div className="about-container about-two-col">
          <div className="about-col-head reveal">
            <span className="about-eyebrow dark">{t('about.story.eyebrow')}</span>
            <h2 className="about-h2">{t('about.story.title')}</h2>
          </div>
          <div className="about-col-body">
            <p className="reveal about-lead-p">{t('about.story.p1')}</p>
            <p className="reveal">{t('about.story.p2')}</p>
            <p className="reveal">{t('about.story.p3')}</p>
            <p className="reveal">{t('about.story.p4')}</p>
            <p className="reveal about-strong-p">{t('about.story.p5')}</p>
          </div>
        </div>

        <div className="about-container">
          <ol className="about-timeline">
            {TIMELINE.map((step, i) => (
              <li key={step.key} className="about-tl-item reveal" style={{ transitionDelay: `${i * 90}ms` }}>
                <span className="about-tl-year">{step.year}</span>
                <span className="about-tl-dot" aria-hidden="true" />
                <h3>{t(`about.${step.key}.title`)}</h3>
                <p>{t(`about.${step.key}.text`)}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="about-section about-mission">
        <div className="about-container">
          <div className="about-center reveal">
            <span className="about-eyebrow">{t('about.mission.eyebrow')}</span>
            <h2 className="about-h2 light">{t('about.mission.title')}</h2>
          </div>
          <div className="about-mission-grid">
            <p className="reveal">{t('about.mission.p1')}</p>
            <p className="reveal">{t('about.mission.p2')}</p>
            <p className="reveal">{t('about.mission.p3')}</p>
          </div>
          <blockquote className="about-quote reveal">{t('about.mission.quote')}</blockquote>
          <ul className="about-values">
            {VALUES.map((k, i) => (
              <li key={k} className="reveal" style={{ transitionDelay: `${i * 90}ms` }}>
                <span className="about-value-num">0{i + 1}</span>
                {t(`about.${k}`)}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="about-section about-vision">
        <div className="about-container about-two-col reverse">
          <div className="about-col-head reveal">
            <span className="about-eyebrow dark">{t('about.vision.eyebrow')}</span>
            <h2 className="about-h2">{t('about.vision.title')}</h2>
            <div className="about-globe" aria-hidden="true">
              <span /><span /><span />
            </div>
          </div>
          <div className="about-col-body">
            <p className="reveal about-lead-p">{t('about.vision.p1')}</p>
            <p className="reveal">{t('about.vision.p2')}</p>
            <p className="reveal">{t('about.vision.p3')}</p>
            <p className="reveal about-ambition">{t('about.vision.ambition')}</p>
          </div>
        </div>
      </section>

      <section className="about-closing">
        <div className="about-container about-center">
          <h2 className="about-closing-title reveal">{t('about.closing.title')}</h2>
          <Link to="/photobooks" className="about-cta reveal">
            {t('about.closing.cta')} <span aria-hidden="true">→</span>
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default About;
